/**
 * Generate timeseries labels based on date range and period type
 * @param {Date} dateFrom - Start date
 * @param {Date} dateTo - End date
 * @param {string} periodType - Type of period: 'daily', 'weekly', 'monthly', 'quarterly'
 * @returns {Array<Date>} Array of date objects
 */
export const generateTimeseriesLabels = (dateFrom, dateTo, periodType) => {
  const labels = [];
  let current = new Date(dateFrom);
  const end = new Date(dateTo);

  while (current <= end) {
    labels.push(new Date(current));

    switch (periodType) {
      case 'daily':
        current.setDate(current.getDate() + 1);
        break;
      case 'weekly':
        current.setDate(current.getDate() + 7);
        break;
      case 'monthly':
        current.setMonth(current.getMonth() + 1);
        break;
      case 'quarterly':
        current.setMonth(current.getMonth() + 3);
        break;
      default:
        current.setDate(current.getDate() + 1);
    }
  }

  return labels;
};

/**
 * Get default date range (last 3 months)
 * @returns {Object} Object with 'from' and 'to' date properties
 */
export const getDefaultDateRange = () => {
  const today = new Date();
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  return { from: threeMonthsAgo, to: today };
};

/**
 * Get period label from index
 * @param {number} index - Index of the period tab (0=daily, 1=weekly, 2=monthly, 3=quarterly)
 * @returns {string} Period label
 */
export const getPeriodLabel = (index) => {
  const labels = ['daily', 'weekly', 'monthly', 'quarterly'];
  return labels[index] || 'daily';
};

/**
 * Get time unit for chart.js time scale
 * @param {string} periodType - Type of period
 * @returns {string} Unit for chart.js
 */
export const getTimeUnit = (periodType) => {
  switch (periodType) {
    case 'daily':
      return 'day';
    case 'weekly':
      return 'week';
    case 'monthly':
      return 'month';
    case 'quarterly':
      return 'quarter';
    default:
      return 'day';
  }
};

/**
 * Get display format for time scale
 * @param {string} periodType - Type of period
 * @returns {Object} Display formats object for chart.js
 */
export const getTimeDisplayFormats = (periodType) => {
  switch (periodType) {
    case 'daily':
      return {
        day: 'MMM dd',
      };
    case 'weekly':
      return {
        week: 'MMM dd',
      };
    case 'monthly':
      return {
        month: 'MMM yyyy',
      };
    case 'quarterly':
      return {
        quarter: '[Q]Q yyyy',
      };
    default:
      return {
        day: 'MMM dd',
      };
  }
};

/**
 * Filter data based on date range
 * @param {Array} data - Array of data objects with date property
 * @param {Date} dateFrom - Start date
 * @param {Date} dateTo - End date
 * @param {string} dateField - Name of the date field in data object
 * @returns {Array} Filtered data
 */
export const filterDataByDateRange = (
  data,
  dateFrom,
  dateTo,
  dateField = 'date',
) => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.filter((item) => {
    const itemDate = new Date(item[dateField]);
    return itemDate >= dateFrom && itemDate <= dateTo;
  });
};
